/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logQnaMakerSmartSuggestions } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AddToastParameters } from 'src/core/utils/decorators/addToast';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerSuggestionsUiStore } from 'src/qnaMaker/appShell/qnaMakeReviewSuggestionsPage/QnaMakerSuggestionsUiStore';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

const messages = defineMessages({
    saveChangesRunning: {
        id: 'qna.useQnaPairsSuggestionActions.saveChangesRunning',
        defaultMessage: 'Saving changes...'
    },
    saveChangesCompleted: {
        id: 'qna.useQnaPairsSuggestionActions.saveChangesCompleted',
        defaultMessage: 'Changes are saved'
    },
    saveChangesFailed: {
        id: 'qna.useQnaPairsSuggestionActions.saveChangesFailed',
        defaultMessage: 'Failed to save changes'
    },
    acceptingSuggestionsRunning: {
        id: 'qna.useQnaPairsSuggestionActions.acceptingSuggestionsRunning',
        defaultMessage: 'Accepting suggestions...'
    },
    rejectingSuggestionsRunning: {
        id: 'qna.useQnaPairsSuggestionActions.rejectingSuggestionsRunning',
        defaultMessage: 'Rejecting suggestions...'
    },
    acceptAllSuggestionsCompletedTitle: {
        id: 'qna.useQnaPairsSuggestionActions.acceptAllSuggestionsCompletedTitle',
        defaultMessage: 'All suggestions accepted'
    },
    acceptSelectedSuggestionsCompletedTitle: {
        id: 'qna.useQnaPairsSuggestionActions.acceptSelectedSuggestionsCompletedTitle',
        defaultMessage: 'Selected suggestions accepted!'
    },
    acceptAllSuggestionsCompletedBody: {
        id: 'qna.useQnaPairsSuggestionActions.acceptAllSuggestionsCompletedBody',
        defaultMessage: 'All suggestions have been accepted and the question pairs removed from the review suggestions page.'
    },
    rejectAllSuggestionsCompletedTitle: {
        id: 'qna.useQnaPairsSuggestionActions.rejectAllSuggestionsCompletedTitle',
        defaultMessage: 'All suggestions rejected'
    },
    rejectSelectedSuggestionsCompletedTitle: {
        id: 'qna.useQnaPairsSuggestionActions.rejectSelectedSuggestionsCompletedTitle',
        defaultMessage: 'Selected suggestions rejected!'
    },
    rejectAllSuggestionsCompletedBody: {
        id: 'qna.useQnaPairsSuggestionActions.rejectAllSuggestionsCompletedBody',
        defaultMessage: 'All suggestions have been deleted and the question pairs removed from the review suggestions page.'
    },
    acceptAllSuggestionsCompleted: {
        id: 'qna.useQnaPairsSuggestionActions.acceptAllSuggestionsCompleted',
        defaultMessage: 'All suggestions accepted'
    },
    acceptSingleSuggestionsCompletedTitle: {
        id: 'qna.useQnaPairsSuggestionActions.acceptSingleSuggestionsCompletedTitle',
        defaultMessage: 'Suggestion accepted'
    },
    acceptSingleSuggestionsCompletedBody: {
        id: 'qna.useQnaPairsSuggestionActions.acceptSingleSuggestionsCompletedBody',
        defaultMessage:
            'The suggestion has been accepted and the question pair removed from the review suggestions page. You can view the changes in the edit page.'
    },
    rejectSingleSuggestionsCompletedTitle: {
        id: 'qna.useQnaPairsSuggestionActions.rejectSingleSuggestionsCompletedTitle',
        defaultMessage: 'Suggestion deleted'
    },
    rejectSingleSuggestionsCompletedBody: {
        id: 'qna.useQnaPairsSuggestionActions.rejectSingleSuggestionsCompletedBody',
        defaultMessage: 'The suggestion has been deleted and the question pair removed from the review suggestions page.'
    }
});

/**
 * A hook for facilitating using question answer pairs handlers.
 */
export const useQnaPairsSuggestionActions = (localStore: QnaMakerSuggestionsUiStore) => {
    const { dispatcher, rootStore } = useGlobalState();
    const { isSmartSuggestionEnabled } = rootStore.currentQnaMakerProjectStore;
    const { localize } = useLocalization();

    const { serviceName } = getPortalFromUrl();
    const savingTracker = useProgressTracker({
        onComplete: () => {
            localStore.setSavingState('saved');
            localStore.setDirtyQnaPairs([]);
        }
    });

    const acceptOrRejectSingleDisplayedQnaPairsSuggestions = React.useCallback(
        (qnaId: number, newData: Partial<QnaMakerQnaSwagger>, isSuggestionAccepted: boolean) => {
            localStore.editDisplayedQnaPair(qnaId, newData);
            localStore.setSavingState('dirty');
            setTimeout(() => {
                saveChanges(true, {
                    running: {
                        title: localize(isSuggestionAccepted ? messages.acceptingSuggestionsRunning : messages.rejectingSuggestionsRunning)
                    },
                    completed: {
                        title: localize(
                            isSuggestionAccepted
                                ? messages.acceptSingleSuggestionsCompletedTitle
                                : messages.rejectAllSuggestionsCompletedTitle
                        ),
                        content: localize(
                            isSuggestionAccepted
                                ? messages.acceptSingleSuggestionsCompletedBody
                                : messages.rejectSingleSuggestionsCompletedBody
                        )
                    }
                });
            }, 0);
        },
        [localStore]
    );

    const acceptOrRejectSelectedQnaPairsSuggestions = React.useCallback(
        (areSuggestionsAccepted: boolean) => {
            if (isSmartSuggestionEnabled) {
                const projectName = rootStore.currentQnaMakerProjectStore.name;
                const { sourcesStoreHouse } = rootStore.currentQnaMakerProjectStore;
                const telemetryData = {
                    projectName,
                    questions: localStore.selectedSuggestionsKeys.map(k => ({
                        qnaId: k,
                        sourceType: sourcesStoreHouse.items.find(
                            s => localStore.qnaPairsJson.find(q => q.id === Number(k))?.source === s.source
                        )?.contentStructureKind,
                        suggestionsCount: localStore.qnaPairsSelectedSuggestionObj[Number(k)].length
                    }))
                };
                logQnaMakerSmartSuggestions(serviceName, areSuggestionsAccepted, telemetryData);
                localStore.acceptOrRejectSelectedQnaPairsSuggestions(areSuggestionsAccepted);
            } else {
                localStore.acceptOrRejectDisplayedQnaPairsSuggestions(areSuggestionsAccepted);
            }
            localStore.setSavingState('dirty');
            setTimeout(() => {
                saveChanges(true, {
                    running: {
                        title: localize(
                            areSuggestionsAccepted ? messages.acceptingSuggestionsRunning : messages.rejectingSuggestionsRunning
                        )
                    },
                    completed: {
                        title: isSmartSuggestionEnabled
                            ? localize(
                                  areSuggestionsAccepted
                                      ? messages.acceptSelectedSuggestionsCompletedTitle
                                      : messages.rejectSelectedSuggestionsCompletedTitle
                              )
                            : localize(
                                  areSuggestionsAccepted
                                      ? messages.acceptAllSuggestionsCompletedTitle
                                      : messages.rejectAllSuggestionsCompletedTitle
                              ),
                        content:
                            !isSmartSuggestionEnabled &&
                            localize(
                                areSuggestionsAccepted
                                    ? messages.acceptAllSuggestionsCompletedBody
                                    : messages.rejectAllSuggestionsCompletedBody
                            )
                    }
                });
            }, 0);
        },
        [localStore]
    );

    const saveChanges = React.useCallback(
        (fetchUpdatedObjects: boolean = true, toastData: AddToastParameters['toastData'] = undefined) => {
            localStore.resetSuggestionSelection();
            localStore.setSavingState('inProgress');
            rootStore.currentQnaMakerProjectStore.resetQnaPairsLoadedState();
            dispatcher.dispatch('saveQnaMakerAssets', {
                fetchUpdatedObjects,
                assetWithOperations: localStore.filteredDirtyQnaPairs,
                tracker: savingTracker,
                toastData: toastData || {
                    running: { title: localize(messages.saveChangesRunning) },
                    completed: { title: localize(messages.saveChangesCompleted) },
                    failed: { title: localize(messages.saveChangesFailed) }
                }
            });
        },
        [dispatcher.dispatch]
    );

    return {
        savingTracker,
        acceptOrRejectSelectedQnaPairsSuggestions,
        acceptOrRejectSingleDisplayedQnaPairsSuggestions
    };
};
